import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import baseUrl from "../api/utils";

const fmt = (n) =>
  (n || n === 0)
    ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const GstSummaryView = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [year, setYear] = useState(now.getFullYear());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseUrl}/reports/gst-summary`, {
        headers: { Authorization: token },
        params: { month, year },
      });
      setSummary(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/unauthorized");
        return;
      }
      console.error("Failed to load GST summary", err);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchSummary();
  }, []);

  const handleExportCsv = () => {
    if (!summary) return;
    const lines = [];
    lines.push(`GST Summary — ${MONTH_NAMES[month - 1]} ${year}`);
    lines.push("");
    lines.push("Summary");
    lines.push(`Total Taxable Value,${summary.totalTaxableValue}`);
    lines.push(`Total CGST,${summary.totalCgst}`);
    lines.push(`Total SGST,${summary.totalSgst}`);
    lines.push(`Total Tax,${summary.totalTax}`);
    lines.push("");
    lines.push("Tax Rate-wise Breakdown");
    lines.push("Tax Rate,Taxable Value,CGST,SGST,Total Tax");
    summary.breakdown.forEach((b) => {
      lines.push(`${b.taxRate}%,${b.taxableValue},${b.cgst},${b.sgst},${b.totalTax}`);
    });
    lines.push(`TOTAL,${summary.totalTaxableValue},${summary.totalCgst},${summary.totalSgst},${summary.totalTax}`);

    const csvContent = lines.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `gst_summary_${year}_${String(month).padStart(2, "0")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const cellStyle = { border: "1px solid #eee", padding: "10px 14px", fontSize: "13px" };
  const thStyle = { ...cellStyle, background: "#f5f6fa", fontWeight: "600", textAlign: "left", color: "#555" };

  const currentYear = now.getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div style={{ background: "#f5f6fa", padding: "20px", borderRadius: "8px" }}>
      {/* ── Month/Year picker + Export ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
          <div>
            <label style={{ fontSize: "11px", color: "#888", fontWeight: "600", display: "block", marginBottom: "4px" }}>Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", background: "#fff" }}
            >
              {MONTH_NAMES.map((name, idx) => (
                <option key={name} value={idx + 1}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: "11px", color: "#888", fontWeight: "600", display: "block", marginBottom: "4px" }}>Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              style={{ padding: "7px 10px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", background: "#fff" }}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={fetchSummary} disabled={loading}>
            {loading ? "Loading..." : "Apply"}
          </button>
        </div>
        <button className="btn btn-danger btn-sm" onClick={handleExportCsv} disabled={!summary || summary.breakdown.length === 0}>
          <i className="fa-solid fa-download mr-1"></i> Export CSV
        </button>
      </div>

      {loading && !summary ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>Loading GST summary...</div>
      ) : !summary ? null : (
        <>
          {/* ── Summary cards ── */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
            {[
              { label: "Taxable Value", value: `₹${fmt(summary.totalTaxableValue)}`, color: "#1976d2" },
              { label: "Total CGST", value: `₹${fmt(summary.totalCgst)}`, color: "#27ae60" },
              { label: "Total SGST", value: `₹${fmt(summary.totalSgst)}`, color: "#27ae60" },
              { label: "Total Tax", value: `₹${fmt(summary.totalTax)}`, color: "#c0392b" },
            ].map((card) => (
              <div key={card.label} style={{
                flex: 1, minWidth: "160px", background: "#fff",
                borderRadius: "10px", padding: "18px 20px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                borderTop: `3px solid ${card.color}`,
              }}>
                <div style={{ fontSize: "11px", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {card.label}
                </div>
                <div style={{ fontSize: "22px", fontWeight: "700", color: "#222", marginTop: "6px" }}>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          {/* ── Tax rate-wise breakdown table ── */}
          <div style={{ background: "#fff", borderRadius: "10px", padding: "18px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", marginBottom: "20px" }}>
            <div style={{ fontSize: "14px", fontWeight: "700", color: "#333", marginBottom: "12px" }}>
              Tax Rate-wise Breakdown — {MONTH_NAMES[month - 1]} {year}
            </div>
            {summary.breakdown.length === 0 ? (
              <div style={{ textAlign: "center", padding: "30px", color: "#aaa", fontSize: "13px" }}>
                No sales with GST found for this month.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Tax Rate</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Taxable Value</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>CGST</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>SGST</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Total Tax</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.breakdown.map((row) => (
                    <tr key={row.taxRate}>
                      <td style={{ ...cellStyle, fontWeight: "600" }}>{row.taxRate}%</td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(row.taxableValue)}</td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(row.cgst)}</td>
                      <td style={{ ...cellStyle, textAlign: "right" }}>₹{fmt(row.sgst)}</td>
                      <td style={{ ...cellStyle, textAlign: "right", fontWeight: "600" }}>₹{fmt(row.totalTax)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: "#f5f6fa" }}>
                    <td style={{ ...cellStyle, fontWeight: "700" }}>TOTAL</td>
                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(summary.totalTaxableValue)}</td>
                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(summary.totalCgst)}</td>
                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(summary.totalSgst)}</td>
                    <td style={{ ...cellStyle, textAlign: "right", fontWeight: "700" }}>₹{fmt(summary.totalTax)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* ── Note ── */}
          <div style={{ fontSize: "12px", color: "#888", background: "#fffbea", border: "1px solid #f0e5b8", borderRadius: "8px", padding: "12px 16px" }}>
            <i className="fa-solid fa-circle-info mr-1"></i>
            This summary reflects intra-state (CGST + SGST) sales recorded in InvoiceBill for the selected month. Use it as a reference when filing your GSTR-1 return — always cross-check against your actual invoices before filing.
          </div>
        </>
      )}
    </div>
  );
};

export default GstSummaryView;